import { useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { capitalizeFirst } from '../../shared/sentence';
import { accountCandidates, exchangeAccountOf, saveExchangeAccount } from './exchangeAccountApi';

/** Wartość listy „z własnych kont” — pusty napis zamiast `null` (Select MUI). */
const OWN = '';

/** Chip z etykietą w kilku liniach — nazwa spółki nie poszerza kolumny. */
const WRAPPING_CHIP = {
  maxWidth: 170,
  height: 'auto',
  py: 0.25,
  '& .MuiChip-label': { whiteSpace: 'normal' },
} as const;

/**
 * „Konto giełdowe” na ekranie Spółek (odpowiedzi Marathonu/Euvic 16.10.2026,
 * pyt. 18b: „póki co jedno konto; zlecenia z obu spółek mają lądować na
 * giełdzie”). Za przełącznikiem `exchangeBothCompanies` (kolumnę dokłada
 * `CompanyList`).
 *
 * Spółka bez własnych kont może publikować z kont innej spółki — wtedy jej
 * zlecenia wolno przekazać do Trade i wystawić, a oferta na giełdzie jest
 * ofertą spółki z kontem. Zmiana z obowiązkowym powodem (dziennik działań);
 * zapis wymaga sekcji administracyjnej — przycisk jej nie ukrywa, API odmówi
 * innym (jak przy „Trade i giełdy”).
 */
export const ExchangeAccount = ({
  record,
  companies,
  onSaved,
}: {
  record: Record<string, unknown>;
  /** Wszystkie spółki z listy — do nazwy wskazanej i do wyboru. */
  companies: ReadonlyArray<Record<string, unknown>>;
  onSaved?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const current = exchangeAccountOf(record);
  const currentName =
    current === null
      ? null
      : ((companies.find((c) => c.id === current)?.name as string | undefined) ?? `spółka nr ${current}`);

  // Chip nad przyciskiem, a nie obok, i etykieta zawijana w wąskiej kolumnie
  // (smoke stagingu 19.10.2026: z tą kolumną ekran Spółek przewijał się
  // w bok o 36 px przy 1366 px; pełna nazwa spółki z kontem — długa,
  // „MARATHON International Sp. z o.o. Sp. k.” — rozpychałaby go dalej).
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }} data-testid="exchange-account">
      {current === null ? (
        <Chip size="small" variant="outlined" label="z własnych kont" />
      ) : (
        <Tooltip title={`Zlecenia tej spółki publikujemy z kont giełdowych spółki ${currentName} — na giełdzie widnieją jako jej oferty.`}>
          <Chip size="small" color="info" variant="outlined" label={`z kont spółki ${currentName}`} sx={WRAPPING_CHIP} />
        </Tooltip>
      )}
      <Button size="small" onClick={() => setOpen(true)}>
        Zmień
      </Button>
      {open && (
        <ExchangeAccountDialog
          record={record}
          companies={companies}
          current={current}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved?.();
          }}
        />
      )}
    </Stack>
  );
};

function ExchangeAccountDialog({
  record,
  companies,
  current,
  onClose,
  onSaved,
}: {
  record: Record<string, unknown>;
  companies: ReadonlyArray<Record<string, unknown>>;
  current: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [target, setTarget] = useState<string>(current === null ? OWN : String(current));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const candidates = accountCandidates(record, companies);
  const unchanged = target === (current === null ? OWN : String(current));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveExchangeAccount(record.id as number, target === OWN ? null : Number(target), reason.trim());
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ? capitalizeFirst(msg) : 'Nie udało się zapisać');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Konto giełdowe — {String(record.name ?? '')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Spółka bez własnych kont giełdowych może publikować z kont innej spółki. Oferta na giełdzie będzie
          wtedy ofertą spółki z kontem — przewoźnik kontaktuje się z nią.
        </Typography>
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel id="konto-gieldowe">Publikuj</InputLabel>
          <Select
            labelId="konto-gieldowe"
            label="Publikuj"
            value={target}
            onChange={(e) => setTarget(String(e.target.value))}
          >
            <MenuItem value={OWN}>z własnych kont</MenuItem>
            {candidates.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                z kont spółki {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Powód"
          required
          fullWidth
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={() => void save()} variant="contained" disabled={busy || unchanged || !reason.trim()}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
